import * as Sentry from '@sentry/react';
import {
  Alert,
  AutoComplete,
  Button,
  Card,
  Checkbox,
  Descriptions,
  Divider,
  Input,
  Space,
  Steps,
  Tag,
  Typography,
} from 'antd';
import { isAxiosError } from 'axios';
import { addCommand, getCommandDetail } from 'api/devices.api';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getDevicePrivilegePresentation,
  type DevicePrivilege,
} from 'libs/devicePrivilege';
import {
  CheckerEmployee,
  CommandTrackingStatus,
  Device,
  ResponseQueueAWS,
} from 'types/devices';

type CommandPurpose = 'verification' | 'promotion' | 'confirmation';

type TrackedCommand = {
  id: number;
  status: CommandTrackingStatus | 'processing';
};

type ActiveCommand = {
  id: number;
  purpose: CommandPurpose;
};

type FlowScreen =
  | 'select'
  | 'review'
  | 'progress'
  | 'success'
  | 'already-administrator'
  | 'failure';

type FlowFailure = {
  kind: 'not-found' | 'error';
  title: string;
  description: string;
};

type DeviceAdministratorFlowProps = {
  device: Device;
  employees: CheckerEmployee[];
  onEmployeesRefresh: () => Promise<void>;
  onClose: () => void;
};

const promotablePrivileges = new Set(['0', '2', '10']);

const isPositiveInteger = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value > 0;

const getTrackedStatus = (
  command: ResponseQueueAWS
): TrackedCommand['status'] => {
  if (command.status === 'pending' && command.queueStatus === 'processing') {
    return 'processing';
  }
  return command.status;
};

const getApiError = (error: unknown, fallback: string): FlowFailure => {
  const apiMessage = isAxiosError<{ message?: string; status?: string }>(error)
    ? error.response?.data?.message
    : undefined;
  const message = apiMessage || fallback;
  const normalizedMessage = message.toLowerCase();

  if (normalizedMessage.includes('no encontr')) {
    return {
      kind: 'not-found',
      title: 'El PIN no fue encontrado',
      description: message,
    };
  }
  if (
    normalizedMessage.includes('venció') ||
    normalizedMessage.includes('vencida') ||
    normalizedMessage.includes('expir')
  ) {
    return {
      kind: 'error',
      title: 'La verificación dejó de ser reciente',
      description: `${message}. Inicia una verificación nueva para continuar.`,
    };
  }

  return {
    kind: 'error',
    title: 'No se pudo completar el cambio',
    description: message,
  };
};

const CommandStatus = ({ command }: { command?: TrackedCommand }) => {
  if (!command) return <Tag>Sin iniciar</Tag>;
  if (command.status === 'successful') {
    return <Tag color="green">ID #{command.id} · Exitoso</Tag>;
  }
  if (command.status === 'error') {
    return <Tag color="red">ID #{command.id} · Error</Tag>;
  }
  if (command.status === 'processing') {
    return <Tag color="blue">ID #{command.id} · Procesando</Tag>;
  }
  return <Tag color="gold">ID #{command.id} · Pendiente</Tag>;
};

export const DeviceAdministratorFlow = ({
  device,
  employees,
  onEmployeesRefresh,
  onClose,
}: DeviceAdministratorFlowProps) => {
  const [pinInput, setPinInput] = useState('');
  const [screen, setScreen] = useState<FlowScreen>('select');
  const [activeCommand, setActiveCommand] = useState<ActiveCommand>();
  const [verification, setVerification] = useState<TrackedCommand>();
  const [promotion, setPromotion] = useState<TrackedCommand>();
  const [confirmation, setConfirmation] = useState<TrackedCommand>();
  const [confirmedPrivilege, setConfirmedPrivilege] =
    useState<DevicePrivilege>();
  const [confirmedName, setConfirmedName] = useState('');
  const [confirmedPin, setConfirmedPin] = useState<number>();
  const [confirmedScope, setConfirmedScope] = useState(false);
  const [failure, setFailure] = useState<FlowFailure>();
  const [queuing, setQueuing] = useState(false);
  const [refreshingEmployees, setRefreshingEmployees] = useState(false);

  const pin = Number(pinInput.trim());
  const validPin = Number.isInteger(pin) && pin > 0;
  const selectedEmployee = employees.find(
    (employee) => Number(employee.PIN) === pin
  );
  const confirmedEmployee = employees.find(
    (employee) => Number(employee.PIN) === confirmedPin
  );
  const confirmedEmployeeName =
    confirmedEmployee?.Name || confirmedName || 'Empleado del dispositivo';
  const privilege = getDevicePrivilegePresentation(confirmedPrivilege);
  const selectedPrivilege = getDevicePrivilegePresentation(
    selectedEmployee?.Pri
  );

  const employeeOptions = useMemo(
    () =>
      employees.map((employee) => ({
        value: String(employee.PIN),
        label: `${employee.PIN} · ${employee.Name || 'Sin nombre'}`,
      })),
    [employees]
  );

  const updateTrackedCommand = useCallback(
    (purpose: CommandPurpose, command: TrackedCommand) => {
      if (purpose === 'verification') setVerification(command);
      if (purpose === 'promotion') setPromotion(command);
      if (purpose === 'confirmation') setConfirmation(command);
    },
    []
  );

  const refreshEmployees = useCallback(async () => {
    setRefreshingEmployees(true);
    try {
      await onEmployeesRefresh();
    } finally {
      setRefreshingEmployees(false);
    }
  }, [onEmployeesRefresh]);

  const queueFinalConfirmation = useCallback(async () => {
    if (!confirmedPin) return;
    try {
      const response = await addCommand({
        serialNumber: device.serialNumber,
        command: 'queryEmployeeInfo',
        employeeId: confirmedPin,
      });
      if (
        response.data.status !== 'queued' ||
        !isPositiveInteger(response.data.commandId)
      ) {
        throw new Error('La consulta final no devolvió un ID válido.');
      }
      const nextCommand = {
        id: response.data.commandId,
        status: 'pending' as const,
      };
      setConfirmation(nextCommand);
      setActiveCommand({
        id: response.data.commandId,
        purpose: 'confirmation',
      });
    } catch (error) {
      Sentry.captureException(error);
      setActiveCommand(undefined);
      setFailure(
        getApiError(
          error,
          'La promoción terminó, pero no fue posible iniciar la consulta final.'
        )
      );
      setScreen('failure');
    }
  }, [confirmedPin, device.serialNumber]);

  useEffect(() => {
    if (!activeCommand) return;

    let cancelled = false;
    let timeoutId: number | undefined;

    const scheduleNextPoll = () => {
      timeoutId = window.setTimeout(pollCommand, 3000);
    };

    const pollCommand = async () => {
      try {
        const response = await getCommandDetail(activeCommand.id);
        if (cancelled) return;

        const command = response.data;
        const status = getTrackedStatus(command);
        updateTrackedCommand(activeCommand.purpose, {
          id: activeCommand.id,
          status,
        });

        if (status === 'pending' || status === 'processing') {
          scheduleNextPoll();
          return;
        }

        if (status === 'error') {
          setActiveCommand(undefined);
          setFailure({
            kind: 'error',
            title: 'No se pudo completar el cambio',
            description:
              command.errorCode ||
              'El dispositivo reportó un error. Puedes iniciar una verificación nueva.',
          });
          setScreen('failure');
          return;
        }

        if (activeCommand.purpose === 'verification') {
          if (command.employeeFound !== true) {
            await refreshEmployees();
            if (cancelled) return;
            setActiveCommand(undefined);
            setFailure({
              kind: 'not-found',
              title: 'El PIN no fue encontrado',
              description: `El dispositivo no reportó el PIN ${confirmedPin}. La lista se actualizó con la ausencia confirmada.`,
            });
            setScreen('failure');
            return;
          }

          const currentPrivilege = command.currentPrivilege;
          const currentPrivilegeInfo =
            getDevicePrivilegePresentation(currentPrivilege);
          setConfirmedPrivilege(currentPrivilege);
          await refreshEmployees();
          if (cancelled) return;
          setActiveCommand(undefined);

          if (currentPrivilegeInfo.administrator) {
            setScreen('already-administrator');
            return;
          }
          if (
            !currentPrivilegeInfo.rawValue ||
            !promotablePrivileges.has(currentPrivilegeInfo.rawValue)
          ) {
            setFailure({
              kind: 'error',
              title: 'El privilegio no tiene un mapeo seguro',
              description: currentPrivilegeInfo.rawValue
                ? `El dispositivo confirmó Pri ${currentPrivilegeInfo.rawValue}. No se enviará una promoción automática.`
                : 'El dispositivo no devolvió un privilegio verificable.',
            });
            setScreen('failure');
            return;
          }
          setScreen('review');
          return;
        }

        if (activeCommand.purpose === 'promotion') {
          setPromotion({ id: activeCommand.id, status: 'successful' });
          await queueFinalConfirmation();
          return;
        }

        await refreshEmployees();
        if (cancelled) return;
        setActiveCommand(undefined);
        if (command.employeeFound !== true) {
          setFailure({
            kind: 'not-found',
            title: 'El empleado ya no existe en el dispositivo',
            description: `La consulta final confirmó que el PIN ${confirmedPin} fue retirado. La fila se eliminó al actualizar la lista.`,
          });
          setScreen('failure');
          return;
        }

        setConfirmedPrivilege(command.currentPrivilege);
        const finalPrivilege = getDevicePrivilegePresentation(
          command.currentPrivilege
        );
        if (!finalPrivilege.administrator) {
          setFailure({
            kind: 'error',
            title: 'El dispositivo no confirmó el nuevo rol',
            description: finalPrivilege.rawValue
              ? `La consulta final reportó Pri ${finalPrivilege.rawValue}. Se conserva ese valor confirmado y puedes reintentar.`
              : 'La consulta final no devolvió un privilegio verificable.',
          });
          setScreen('failure');
          return;
        }
        setScreen('success');
      } catch (error) {
        if (cancelled) return;
        Sentry.captureException(error);
        setActiveCommand(undefined);
        setFailure(
          getApiError(error, 'No fue posible consultar el estado del comando.')
        );
        setScreen('failure');
      }
    };

    void pollCommand();

    return () => {
      cancelled = true;
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, [
    activeCommand,
    confirmedPin,
    queueFinalConfirmation,
    refreshEmployees,
    updateTrackedCommand,
  ]);

  const verifyEmployee = async () => {
    if (!validPin) return;
    setQueuing(true);
    setFailure(undefined);
    setConfirmedPin(pin);
    setConfirmedName(selectedEmployee?.Name || 'Empleado del dispositivo');
    setConfirmedPrivilege(undefined);
    setConfirmedScope(false);
    setPromotion(undefined);
    setConfirmation(undefined);
    try {
      const response = await addCommand({
        serialNumber: device.serialNumber,
        command: 'queryEmployeeInfo',
        employeeId: pin,
      });
      if (
        response.data.status !== 'queued' ||
        !isPositiveInteger(response.data.commandId)
      ) {
        throw new Error('La verificación no devolvió un ID válido.');
      }
      const queuedVerification = {
        id: response.data.commandId,
        status: 'pending' as const,
      };
      setVerification(queuedVerification);
      setActiveCommand({
        id: response.data.commandId,
        purpose: 'verification',
      });
    } catch (error) {
      Sentry.captureException(error);
      setFailure(
        getApiError(error, 'No fue posible iniciar la verificación del PIN.')
      );
      setScreen('failure');
    } finally {
      setQueuing(false);
    }
  };

  const promoteEmployee = async () => {
    if (!confirmedPin || !verification || !confirmedScope) return;
    setQueuing(true);
    try {
      const response = await addCommand({
        serialNumber: device.serialNumber,
        command: 'promoteDeviceAdministrator',
        employeeId: confirmedPin,
        verificationCommandId: verification.id,
      });

      if (
        response.data.status === 'already_administrator' &&
        response.data.currentPrivilege !== undefined
      ) {
        setConfirmedPrivilege(response.data.currentPrivilege);
        setScreen('already-administrator');
        return;
      }
      if (
        response.data.status !== 'queued' ||
        !isPositiveInteger(response.data.commandId)
      ) {
        throw new Error('La promoción no devolvió un ID válido.');
      }

      const queuedPromotion = {
        id: response.data.commandId,
        status: 'pending' as const,
      };
      setPromotion(queuedPromotion);
      setActiveCommand({
        id: response.data.commandId,
        purpose: 'promotion',
      });
      setScreen('progress');
    } catch (error) {
      Sentry.captureException(error);
      setFailure(getApiError(error, 'No fue posible encolar la promoción.'));
      setScreen('failure');
    } finally {
      setQueuing(false);
    }
  };

  const resetFlow = () => {
    setActiveCommand(undefined);
    setVerification(undefined);
    setPromotion(undefined);
    setConfirmation(undefined);
    setConfirmedPrivilege(undefined);
    setConfirmedName('');
    setConfirmedPin(undefined);
    setConfirmedScope(false);
    setFailure(undefined);
    setScreen('select');
  };

  const currentStep = screen === 'select' ? 0 : screen === 'review' ? 1 : 2;
  const isVerifying = activeCommand?.purpose === 'verification';

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Steps
        size="small"
        current={currentStep}
        items={[
          { title: 'Seleccionar y verificar' },
          { title: 'Revisar y confirmar' },
          { title: 'Seguir y confirmar' },
        ]}
      />

      {screen === 'select' && (
        <>
          <div>
            <Typography.Text strong>Empleado del dispositivo</Typography.Text>
            <AutoComplete
              options={employeeOptions}
              value={pinInput}
              disabled={isVerifying}
              onChange={setPinInput}
              filterOption={(inputValue, option) =>
                String(option?.label || '')
                  .toLowerCase()
                  .includes(inputValue.toLowerCase())
              }
              style={{ width: '100%', marginTop: 8 }}
              data-cy="device-administrator-employee"
            >
              <Input
                inputMode="numeric"
                placeholder="Buscar o ingresar número de empleado (PIN)"
              />
            </AutoComplete>
          </div>

          {validPin && selectedEmployee && (
            <Card size="small" data-cy="device-administrator-current-role">
              <Space direction="vertical" size={2}>
                <Typography.Text type="secondary">
                  Último dato descargado
                </Typography.Text>
                <Typography.Text strong>
                  {selectedEmployee.Name || 'Sin nombre'} · PIN {pin}
                </Typography.Text>
                <Typography.Text>
                  {selectedPrivilege.rawValue
                    ? `Pri ${selectedPrivilege.rawValue} · ${selectedPrivilege.label}`
                    : selectedPrivilege.label}
                </Typography.Text>
              </Space>
            </Card>
          )}

          {validPin && !selectedEmployee && (
            <Alert
              type="warning"
              showIcon
              message={`El PIN ${pin} no aparece en la lista actual`}
              description="Puedes actualizar la lista o verificarlo directamente. La promoción solo se habilitará si el dispositivo confirma que existe."
              action={
                <Button
                  size="small"
                  loading={refreshingEmployees}
                  onClick={() => void refreshEmployees()}
                >
                  Actualizar lista
                </Button>
              }
            />
          )}

          <Alert
            type="info"
            showIcon
            message="Se consultará el PIN directamente"
            description="La lista ayuda a seleccionar; no sustituye una verificación reciente del dispositivo. No se solicita contraseña ni información biométrica."
          />

          {verification && <CommandStatus command={verification} />}

          <Space wrap>
            <Button
              type="primary"
              loading={queuing || isVerifying}
              disabled={!validPin || isVerifying}
              onClick={() => void verifyEmployee()}
              data-cy="device-administrator-verify"
            >
              {isVerifying ? 'Verificando empleado…' : 'Verificar empleado'}
            </Button>
            <Button
              onClick={isVerifying ? resetFlow : onClose}
              disabled={queuing}
              data-cy="device-administrator-cancel-verification"
            >
              {isVerifying ? 'Cancelar verificación' : 'Cancelar'}
            </Button>
          </Space>
        </>
      )}

      {screen === 'review' && confirmedPin && (
        <>
          <Alert
            type="success"
            showIcon
            message="Empleado confirmado por el dispositivo"
            description={`Consulta #${verification?.id}`}
          />
          <Card size="small">
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Empleado">
                {confirmedEmployeeName}
              </Descriptions.Item>
              <Descriptions.Item label="PIN">{confirmedPin}</Descriptions.Item>
              <Descriptions.Item label="Rol actual">
                {privilege.rawValue
                  ? `Pri ${privilege.rawValue} · ${privilege.label}`
                  : privilege.label}
              </Descriptions.Item>
            </Descriptions>
          </Card>
          <Alert
            type="warning"
            showIcon
            message="Esta acción otorga acceso al menú local"
            description="La persona podrá administrar opciones del checador y registrar biométricos físicamente en el equipo. No cambia su rol ni permisos en ProSinergia."
          />
          <Checkbox
            checked={confirmedScope}
            onChange={(event) => setConfirmedScope(event.target.checked)}
            data-cy="device-administrator-confirm-scope"
          >
            Entiendo que el cambio solo afecta a este dispositivo.
          </Checkbox>
          <Card size="small">
            <Typography.Text type="secondary">
              Resultado esperado
            </Typography.Text>
            <br />
            <Typography.Text strong>
              Pri {privilege.rawValue} · {privilege.label} → Pri 6 ·
              Administrador
            </Typography.Text>
          </Card>
          <Space wrap>
            <Button
              type="primary"
              loading={queuing}
              disabled={!confirmedScope}
              onClick={() => void promoteEmployee()}
              data-cy="device-administrator-promote"
            >
              Convertir en administrador
            </Button>
            <Button onClick={onClose}>Cancelar</Button>
          </Space>
          <Typography.Text type="secondary">
            La confirmación vence si la verificación deja de ser reciente.
          </Typography.Text>
        </>
      )}

      {screen === 'progress' && (
        <>
          <Alert
            type="info"
            showIcon
            message="Cambio de rol en proceso"
            description="Encolado, ejecutado y confirmado por el dispositivo son estados distintos. La tabla conserva el último valor confirmado."
          />
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Card size="small" title="1. Verificación previa">
              <CommandStatus command={verification} />
            </Card>
            <Card size="small" title="2. Promoción a administrador">
              <CommandStatus command={promotion} />
              {promotion?.status === 'successful' && (
                <Typography.Paragraph type="secondary" className="mt-3 mb-0">
                  El ACK no confirma todavía que Pri cambió.
                </Typography.Paragraph>
              )}
            </Card>
            <Card size="small" title="3. Consulta final del rol">
              <CommandStatus command={confirmation} />
            </Card>
          </Space>
          <Space wrap>
            <Button disabled loading={Boolean(activeCommand)}>
              Esperando dispositivo…
            </Button>
            <Button
              onClick={
                activeCommand?.purpose === 'confirmation' ? resetFlow : onClose
              }
              data-cy="device-administrator-cancel-confirmation"
            >
              {activeCommand?.purpose === 'confirmation'
                ? 'Cancelar verificación'
                : 'Cerrar'}
            </Button>
          </Space>
        </>
      )}

      {screen === 'already-administrator' && (
        <>
          <Alert
            type="success"
            showIcon
            message="Ya es administrador del dispositivo"
            description={`El dispositivo confirmó Pri ${privilege.rawValue} · ${privilege.label}. No se creó un comando de promoción ni se modificó el privilegio.`}
            data-cy="device-administrator-already"
          />
          <Button onClick={onClose}>Cerrar</Button>
        </>
      )}

      {screen === 'success' && (
        <>
          <Alert
            type="success"
            showIcon
            message="Rol confirmado por el dispositivo"
            description={`${confirmedEmployeeName} ahora aparece como Pri ${privilege.rawValue} · ${privilege.label}. Consulta final #${confirmation?.id}.`}
            data-cy="device-administrator-success"
          />
          <Button onClick={onClose}>Cerrar</Button>
        </>
      )}

      {screen === 'failure' && failure && (
        <>
          <Alert
            type={failure.kind === 'error' ? 'error' : 'warning'}
            showIcon
            message={failure.title}
            description={failure.description}
            data-cy="device-administrator-failure"
          />
          <Divider />
          <Typography.Text type="secondary">
            El último Pri confirmado permanece sin cambios.
          </Typography.Text>
          <Space wrap>
            <Button
              type="primary"
              onClick={resetFlow}
              data-cy="device-administrator-retry"
            >
              Reintentar verificación
            </Button>
            <Button onClick={onClose}>Cerrar</Button>
          </Space>
        </>
      )}
    </Space>
  );
};
